'use client';

import Image from 'next/image';
import { CheckCircleIcon } from '@heroicons/react/24/outline';

import { Text } from '@northladder/i18n';
import {
  TAssessmentImageType,
  TImageMetaPosition,
  TImageMetaPositionAlt,
} from '@northladder/apis';
import { convertToHumanReadableTimestamp } from '@northladder/utilities';

import { apiTranslations } from '../../translations';

import { IDeviceImageOptionsWithMetadataTranslations } from '../types';
import { useTargetAssessmentConfig } from '../hooks';

type TDoneUploadingV2Props = Pick<
  IDeviceImageOptionsWithMetadataTranslations,
  'title' | 'titlePostfix'
> & {
  imageId: TAssessmentImageType | TImageMetaPositionAlt | TImageMetaPosition;
  imagePath: string;
  uploadedOn?: number;
};

/**
 * -----------------------------------------------------------------------------
 * Displays that the image has already been uploaded.
 * It will show only the upload status is `COMPLETED`
 *
 * - See also `PendingUploadingV2` & `PendingReUpload`
 */
export function DoneUploadingV2({
  imageId,
  imagePath,
  title,
  titlePostfix,
  uploadedOn,
}: TDoneUploadingV2Props) {
  const thumbnailUrl = useTargetAssessmentConfig(imageId).thumbnail;

  return (
    <div
      className="
        space-between xsm:flex-1 xsm:w-1/2 group relative flex min-h-[400px] w-full
        flex-col items-center overflow-hidden rounded-lg border border-green-100
        bg-green-200/20 py-4 text-center transition-all  duration-300
        ease-in
      "
    >
      {imagePath ? (
        <Image
          // @ts-ignore - Ignoring type errors for complicating accessibility with string checks.
          alt={`Already uploaded device image for ${title?.en || imageId}`}
          className="absolute z-10 -m-4 w-full object-contain object-center"
          height={1200}
          src={imagePath}
          width={600}
        />
      ) : null}

      <div className="z-20 flex flex-1 flex-col justify-center">
        {!imagePath ? (
          <Image
            alt="Already upload device image"
            className="my-10 h-40 w-full object-contain object-center py-4"
            height={1200}
            src={thumbnailUrl}
            width={600}
          />
        ) : null}
        <div
          className="
            absolute right-2 top-2 flex items-center justify-center rounded-full
            border-2 border-green-500 bg-green-700 px-2 py-1 text-xs text-white
          "
        >
          <CheckCircleIcon
            className="
              -ml-1 h-5 w-5 self-center stroke-white stroke-2 ltr:mr-1 rtl:-mr-1
              rtl:ml-1
            "
          />
          <Text text={apiTranslations.uploadDone.uploadDoneTitle} />
        </div>
      </div>
      <div className="z-20 -mb-4 w-full flex-col bg-gray-900/40 px-4 py-2  text-sm">
        <p
          className="
            flex w-full flex-col items-start pb-1 pt-2 text-sm
            text-white
          "
        >
          {title ? (
            <span>
              <Text text={title} />
            </span>
          ) : null}

          {titlePostfix ? (
            <span className="text-xs font-light">
              <Text text={titlePostfix} />
            </span>
          ) : null}

          {uploadedOn ? (
            <span className="text-primary-50 mt-1 gap-2 text-xs font-light leading-3">
              <Text className="mr-1" text="Uploaded" />
              <Text text={convertToHumanReadableTimestamp(uploadedOn)} />
            </span>
          ) : null}
        </p>
      </div>
    </div>
  );
}
